import { createRouter, createWebHistory } from 'vue-router'
import PortalHome from '@/views/portal-home.vue'
import UserCenter from '@/views/userCenter/user-center.vue'
import Workbench from '@/views/workbench/workbench.vue'
import Signature from '@/views/copyrightProtection/Signature.vue'
import { isLoggedIn } from '@/utils/auth'
import { ElMessage } from 'element-plus'
import { t } from '@/utils/i18n'
import { updateDocumentTitle } from '@/utils/documentTitle'
import { installChunkLoadErrorHandler } from '@/utils/chunkLoadError'
const MAINTENANCE_MODE_FLAG = process.env.VUE_APP_MAINTENANCE_MODE
const MAINTENANCE_MODE = MAINTENANCE_MODE_FLAG === true || MAINTENANCE_MODE_FLAG === 'true'
/** 门户首页地址：域名根路径 /，不再带 /home/recommendation */
const DEFAULT_HOME = '/'


const routes = [
  // ---------- 根路径：直接渲染「为你推荐」，地址栏保持 / ----------
  ...(MAINTENANCE_MODE
    ? [{ path: '/', redirect: '/maintenance' }]
    : [{
        path: '/',
        name: 'PortalHomeRoot',
        component: PortalHome,
        meta: { titleKey: 'route.portalHome', title: '门户首页' },
        children: [
          {
            path: '',
            name: 'HomeRecommendation',
            meta: { titleKey: 'route.forYou', title: '为你推荐' },
            component: () => import('@/views/ForYouRecommendation.vue')
          }
        ]
      }]),

  // ---------- 门户首页（业务子路由仍挂在 /home/...） ----------
  {
    path: '/home',
    name: 'PortalHome',
    component: PortalHome,
    redirect: DEFAULT_HOME,
    meta: { titleKey: 'route.portalHome', title: '门户首页' },
    children: [
      // 兼容旧链接 /home/recommendation → /
      {
        path: 'recommendation',
        redirect: DEFAULT_HOME
      },
      // 教育培训
      {
        path: 'education',
        component: () => import('@/views/education/Education.vue'),
        meta: { titleKey: 'route.education', title: '教育培训' },
        children: [
          {
            path: '',
            name: 'HomeEducation',
            meta: { titleKey: 'route.knowledgeClass', title: '知识课堂' },
            component: () => import('@/views/education/KnowledgeClassroom.vue')
          },
          {
            path: 'practice',
            name: 'EducationPractice',
            meta: { titleKey: 'route.practice', title: '刷题练习' },
            component: () => import('@/views/education/PracticeQuestions.vue')
          },
          {
            path: 'practice/:id/take',
            name: 'EducationPracticeTake',
            meta: { titleKey: 'route.practice', title: '刷题练习' },
            component: () => import('@/views/education/PracticeTake.vue')
          },
          {
            path: 'practice/:id',
            name: 'EducationPracticeDetail',
            meta: { titleKey: 'route.practiceDetail', title: '练习详情' },
            component: () => import('@/views/education/PracticeDetail.vue')
          },
          {
            path: 'exam',
            name: 'EducationExam',
            meta: { titleKey: 'route.onlineExam', title: '在线考试' },
            component: () => import('@/views/education/ExamList.vue')
          },
          {
            path: 'exam/:id/take',
            name: 'EducationExamTake',
            meta: { titleKey: 'route.onlineExam', title: '在线考试' },
            component: () => import('@/views/education/ExamTake.vue')
          },
          {
            path: 'exam/:id/records/:recordId',
            name: 'EducationExamRecordDetail',
            meta: { titleKey: 'route.examRecordDetail', title: '考试记录详情' },
            component: () => import('@/views/education/ExamRecordDetail.vue')
          },
          {
            path: 'exam/:id/records',
            name: 'EducationExamRecords',
            meta: { titleKey: 'route.examRecords', title: '考试记录' },
            component: () => import('@/views/education/ExamRecords.vue')
          },
          {
            path: 'exam/:id/wrong',
            name: 'EducationExamWrong',
            meta: { titleKey: 'route.wrongPractice', title: '错题练习' },
            component: () => import('@/views/education/ExamWrongPractice.vue')
          },
          {
            path: 'exam/:id',
            name: 'EducationExamDetail',
            meta: { titleKey: 'route.examDetail', title: '考试详情' },
            component: () => import('@/views/education/ExamDetail.vue')
          },
          {
            path: 'certificate',
            name: 'EducationCertificate',
            meta: { titleKey: 'route.myCertificates', title: '我的证书' },
            component: () => import('@/views/education/MyCertificates.vue')
          },
          {
            path: 'course/:id',
            name: 'EducationCourseDetail',
            meta: { titleKey: 'route.courseDetail', title: '课程详情' },
            component: () => import('@/views/education/CourseDetail.vue')
          },
          {
            path: 'course/:id/learn',
            name: 'EducationCourseLearn',
            meta: { titleKey: 'route.courseLearn', title: '课程学习' },
            component: () => import('@/views/education/CourseLearn.vue')
          }
        ]
      },
      // 版权保护-原创作品备案
      {
        path: 'copyright-protection/original-work-filing',
        name: 'OriginalWorkFiling',
        meta: { titleKey: 'route.originalWorkFiling', title: '原创作品备案', filingType: '0' },
        component: () => import('@/views/copyrightProtection/originalWorkFiling/OriginalWorkFiling.vue')
      },
      // 版权保护-产品发布存证
      {
        path: 'copyright-protection/product-launch-filing',
        name: 'ProductLaunchFiling',
        meta: { titleKey: 'route.productLaunchFiling', title: '产品发布存证', filingType: '2' },
        component: () => import('@/views/copyrightProtection/originalWorkFiling/OriginalWorkFiling.vue')
      },
      // 版权保护-知识产权查询
      {
        path: 'copyright-protection/intellectual-property-query',
        name: 'IntellectualPropertyQuery',
        meta: { titleKey: 'route.ipQuery', title: '知识产权查询' },
        component: () => import('@/views/copyrightProtection/IntellectualPropertyQuery.vue')
      },
      // 版权保护-知识产权管理（门户内介绍页，与顶栏「知识产权管理」入口一致）
      {
        path: 'copyright-protection/intellectual-property-management',
        name: 'IntellectualPropertyManagementIntro',
        meta: { titleKey: 'route.ipManagement', title: '知识产权管理' },
        component: () => import('@/views/copyrightProtection/IntellectualPropertyManagement/IntellectualPropertyManagementIntro.vue')
      },
      // 版权保护-自助取证/固证
      {
        path: 'copyright-protection/evidence-collection',
        name: 'EvidenceCollection',
        meta: { titleKey: 'route.evidenceCollection', title: '自助取证/固证' },
        component: () => import('@/views/copyrightProtection/evidenceCollection/EvidenceCollectionIndex.vue')
      },
      // 版权服务机构-版权社会组织
      {
        path: 'copyright-service-organizations/copyright-social-organization',
        name: 'CopyrightSocialOrganization',
        meta: { titleKey: 'route.copyrightSocialOrg', title: '版权社会组织' },
        component: () => import('@/views/copyrightServiceOrganizations/CopyrightSocialOrganization.vue')
      },
      // 通用占位页 - 首页占位
      {
        path: 'placeholder',
        name: 'HomePlaceholder',
        meta: { titleKey: 'route.homePlaceholder', title: '首页占位' },
        component: () => import('@/views/Placeholder.vue')
      },
      // 版权服务（二级菜单入口重定向到首项）
      {
        path: 'copyright-services',
        redirect: { name: 'copyrightServiceContentConfigIndex' }
      },
      {
        path: 'copyright-services/work-copyright-registration',
        name: 'copyrightServiceContentConfigIndex',
        meta: { titleKey: 'route.workCopyrightRegistration', title: '作品著作权登记', featureKey: 'work_registration' },
        component: () => import('@/views/copyrightService/CopyrightServiceContentConfigIndex.vue')
      },
      {
        path: 'copyright-services/software-copyright-registration',
        name: 'SoftwareCopyrightRegistration',
        meta: { titleKey: 'route.softwareCopyrightRegistration', title: '软件著作权登记', featureKey: 'software_registration' },
        component: () => import('@/views/copyrightService/CopyrightServiceContentConfigIndex.vue')
      },
      {
        path: 'copyright-services/tsa-evidence-solidification',
        name: 'TsaEvidenceSolidification',
        meta: { titleKey: 'route.tsaEvidence', title: 'TSA证据固化', featureKey: 'tsaEvidence' },
        component: () => import('@/views/copyrightService/CopyrightServiceContentConfigIndex.vue')
      },
      {
        path: 'copyright-services/ip-dispute-mediation',
        name: 'IpDisputeMediation',
        meta: { titleKey: 'route.ipDisputeMediation', title: '知识产权纠纷调解', featureKey: 'mediation' },
        component: () => import('@/views/copyrightService/CopyrightServiceContentConfigIndex.vue')
      },
      {
        path: 'copyright-services/copyright-appraisal-comparison',
        name: 'CopyrightAppraisalComparison',
        meta: { titleKey: 'route.copyrightAppraisal', title: '版权鉴定比对', featureKey: 'appraisal' },
        component: () => import('@/views/copyrightService/CopyrightServiceContentConfigIndex.vue')
      },
      {
        path: 'copyright-services/copyright-compliance-guidance',
        name: 'CopyrightComplianceGuidance',
        meta: { titleKey: 'route.copyrightCompliance', title: '版权合规辅导', featureKey: 'copyrightCompliance' },
        component: () => import('@/views/copyrightService/CopyrightServiceContentConfigIndex.vue')
      },
      {
        path: 'copyright-services/copyright-training-cooperation',
        name: 'CopyrightTrainingCooperation',
        meta: { titleKey: 'route.copyrightTraining', title: '版权培训合作', featureKey: 'copyrightTraining' },
        component: () => import('@/views/copyrightService/CopyrightServiceContentConfigIndex.vue')
      },
      {
        path: 'copyright-services/platform-infringement-complaint',
        name: 'PlatformInfringementComplaint',
        meta: { titleKey: 'route.infringementAppeal', title: '平台侵权申诉', featureKey: 'infringementAppeal' },
        component: () => import('@/views/copyrightService/CopyrightServiceContentConfigIndex.vue')
      },
      // 授权交易
      {
        path: 'authorization-trading',
        name: 'AuthorizationTrading',
        redirect: { name: 'FindIp' }
      },
      {
        path: 'authorization-trading/find-ip',
        name: 'FindIp',
        meta: { titleKey: 'route.findIp', title: '找IP', featureKey: 'find_ip', module: 'ip' },
        component: () => import('@/views/authorizationTrading/FindIpList.vue')
      },
      {
        path: 'authorization-trading/find-brand',
        name: 'FindBrand',
        meta: { titleKey: 'route.findBrand', title: '找品牌', featureKey: 'find_brand', module: 'brand' },
        component: () => import('@/views/authorizationTrading/FindIpList.vue')
      },
      {
        path: 'authorization-trading/find-supply',
        name: 'FindSupply',
        meta: { titleKey: 'route.findSupply', title: '找供应链', featureKey: 'find_supply', module: 'supply' },
        component: () => import('@/views/authorizationTrading/FindIpList.vue')
      },
      {
        path: 'authorization-trading/ip/:id',
        name: 'FindIpDetail',
        meta: { titleKey: 'route.ipDetail', title: 'IP详情', featureKey: 'find_ip', module: 'ip' },
        component: () => import('@/views/authorizationTrading/FindIpDetail.vue')
      },
      {
        path: 'authorization-trading/brand/:id',
        name: 'FindBrandDetail',
        meta: { titleKey: 'route.brandDetail', title: '品牌详情', featureKey: 'find_brand', module: 'brand' },
        component: () => import('@/views/authorizationTrading/FindBrandDetail.vue')
      },
      {
        path: 'authorization-trading/supply/:id',
        name: 'FindSupplyDetail',
        meta: { titleKey: 'route.supplyDetail', title: '供应链详情', featureKey: 'find_supply', module: 'supply' },
        component: () => import('@/views/authorizationTrading/FindSupplyDetail.vue')
      },
      {
        path: 'authorization-trading/ip-case/:id',
        name: 'IpCaseDetail',
        meta: { titleKey: 'route.derivativeCaseDetail', title: '衍生作品案例详情', featureKey: 'find_ip', module: 'ip' },
        component: () => import('@/views/authorizationTrading/IpCaseDetail.vue')
      },
      // 全球版权资讯
      {
        path: 'global-copyright-news',
        redirect: '/home/global-copyright-news/frontier-report'
      },
      {
        path: 'global-copyright-news/professional-news',
        name: 'GlobalProfessionalNews',
        meta: { titleKey: 'route.professionalNews', title: '全球专业资讯', newsType: '0' },
        component: () => import('@/views/globalCopyrightNews/GlobalCopyrightNewsList.vue')
      },
      {
        path: 'global-copyright-news/professional-news/:id',
        name: 'GlobalProfessionalNewsDetail',
        meta: { titleKey: 'route.professionalNewsDetail', title: '全球专业资讯详情', newsType: '0' },
        component: () => import('@/views/globalCopyrightNews/GlobalCopyrightNewsDetail.vue')
      },
      {
        path: 'global-copyright-news/licensing-news',
        name: 'GlobalLicensingNews',
        meta: { titleKey: 'route.licensingNews', title: '全球授权资讯', newsType: '1' },
        component: () => import('@/views/globalCopyrightNews/GlobalCopyrightNewsList.vue')
      },
      {
        path: 'global-copyright-news/licensing-news/:id',
        name: 'GlobalLicensingNewsDetail',
        meta: { titleKey: 'route.licensingNewsDetail', title: '全球授权资讯详情', newsType: '1' },
        component: () => import('@/views/globalCopyrightNews/GlobalCopyrightNewsDetail.vue')
      },
      {
        path: 'global-copyright-news/frontier-report',
        name: 'GlobalFrontierReport',
        meta: { titleKey: 'route.frontierReport', title: '全球前沿报告' },
        component: () => import('@/views/globalCopyrightNews/GlobalFrontierReport.vue')
      },
      {
        path: 'global-copyright-news/deep-analysis',
        name: 'GlobalDeepAnalysis',
        meta: { titleKey: 'route.deepAnalysis', title: '深度解析', newsType: '2' },
        component: () => import('@/views/globalCopyrightNews/GlobalCopyrightNewsList.vue')
      },
      {
        path: 'global-copyright-news/deep-analysis/:id',
        name: 'GlobalDeepAnalysisDetail',
        meta: { titleKey: 'route.deepAnalysisDetail', title: '深度解析详情', newsType: '2' },
        component: () => import('@/views/globalCopyrightNews/GlobalCopyrightNewsDetail.vue')
      },
      // 行业交流&活动
      {
        path: 'industry-communication-events',
        name: 'IndustryCommunicationEvents',
        meta: { titleKey: 'route.industryEvents', title: '行业交流&活动' },
        component: () => import('@/views/industryCommunicationEvents/ActivityList.vue')
      },
      {
        path: 'industry-communication-events/:id',
        name: 'IndustryCommunicationEventDetail',
        meta: { titleKey: 'route.activityDetail', title: '活动详情' },
        component: () => import('@/views/industryCommunicationEvents/ActivityDetail.vue')
      },
      // 法规&资助政策
      {
        path: 'regulations-funding-policies',
        name: 'RegulationsFundingPolicies',
        meta: { titleKey: 'route.regulations', title: '法规&资助政策' },
        component: () => import('@/views/regulationsFundingPolicies/RegulationsFundingList.vue')
      },
      {
        path: 'regulations-funding-policies/:id',
        name: 'RegulationsFundingPolicyDetail',
        meta: { titleKey: 'route.regulationsDetail', title: '法规&资助政策详情' },
        component: () => import('@/views/regulationsFundingPolicies/RegulationsFundingDetail.vue')
      },
      // 版权产业分布
      {
        path: 'copyright-industry-distribution',
        name: 'CopyrightIndustryDistribution',
        meta: { titleKey: 'route.industryDistribution', title: '版权产业分布' },
        component: () => import('@/views/Placeholder.vue')
      },
      // 二级菜单未实现 - 版权保护
      // 版权预审
      {
        path: 'copyright-protection/copyright-pre-examination',
        name: 'CopyrightPreExamination',
        meta: { titleKey: 'route.copyrightPreExamination', title: '版权预审' },
        component: () => import('@/views/Placeholder.vue')
      },
      
      // 版权保护-版权风险监测
      {
        path: 'copyright-protection/copyright-risk-monitoring',
        name: 'CopyrightRiskMonitoring',
        meta: { titleKey: 'route.copyrightRiskMonitoring', title: '版权风险监测' },
        component: () => import('@/views/copyrightProtection/copyrightRiskMonitoring/CopyrightRiskMonitoringIndex.vue')
      },
      // 合同模版
      {
        path: 'copyright-protection/contract-templates',
        name: 'ContractTemplates',
        meta: { titleKey: 'route.contractTemplates', title: '合同模版' },
        component: () => import('@/views/copyrightProtection/ContractTemplates.vue')
      },
      // 免费咨询
      {
        path: 'copyright-protection/free-consultation',
        name: 'FreeConsultation',
        meta: { titleKey: 'route.freeConsultation', title: '免费咨询' },
        component: () => import('@/views/copyrightProtection/freeConsultation/FreeConsultation.vue')
      },
      // 时间戳验证
      {
        path: 'copyright-protection/timestamp-verification',
        name: 'TimestampVerification',
        meta: { titleKey: 'route.timestampVerify', title: '时间戳验证' },
        component: () => import('@/views/copyrightProtection/TimestampVerification.vue')
      },
      // 二级菜单未实现 - 版权服务机构
      // （优秀）版权专业律所
      {
        path: 'copyright-service-organizations/excellent-copyright-law-firms',
        name: 'ExcellentCopyrightLawFirms',
        meta: { titleKey: 'route.excellentLawFirms', title: '（优秀）版权专业律所' },
        component: () => import('@/views/Placeholder.vue')
      },
      // （优秀）版权咨询服务机构
      {
        path: 'copyright-service-organizations/excellent-copyright-consulting-agencies',
        name: 'ExcellentCopyrightConsultingAgencies',
        meta: { titleKey: 'route.excellentConsulting', title: '（优秀）版权咨询服务机构' },
        component: () => import('@/views/Placeholder.vue')
      },
      // 兜底重定向（无 component，不设 title）
      // 版权保护未配置路径 → 占位页
      {
        path: 'copyright-protection/:pathMatch(.*)*',
        redirect: '/home/placeholder'
      },
      // 版权服务机构未配置路径 → 占位页
      {
        path: 'copyright-service-organizations/:pathMatch(.*)*',
        redirect: '/home/placeholder'
      },
      // 其他未配置路径 → 占位页
      {
        path: ':pathMatch(.*)*',
        redirect: '/home/placeholder'
      }
    ]
  },
   // ---------- 系统维护页（无需登录） ----------
   {
    path: '/maintenance',
    name: 'Maintenance',
    component: () => import('@/views/Maintenance.vue'),
    meta: { titleKey: 'route.maintenance', title: '系统维护中', requiresAuth: false }
  },

  // ---------- 用户中心（需登录） ----------
  {
    path: '/user-center',
    name: 'UserCenter',
    component: UserCenter,
    redirect: '/user-center/account/personal-info',
    meta: { titleKey: 'route.userCenter', title: '用户中心', requiresAuth: true },
    children: [
      // 个人实名认证
      {
        path: 'account/personal-auth',
        name: 'PersonalAuth',
        meta: { titleKey: 'route.personalAuth', title: '个人实名认证' },
        component: () => import('@/views/userCenter/account/PersonalIdentityVerification.vue')
      },
      // 企业身份认证
      {
        path: 'account/enterprise-auth',
        name: 'EnterpriseAuth',
        meta: { titleKey: 'route.enterpriseAuth', title: '企业身份认证' },
        component: () => import('@/views/userCenter/account/EnterpriseIdentityVerification.vue')
      },
      // 企业管理员
      {
        path: 'account/management',
        name: 'EnterpriseAdminManagement',
        meta: { titleKey: 'route.enterpriseAdmin', title: '企业管理员' },
        component: () => import('@/views/userCenter/account/EnterpriseAdminManagement.vue')
      },
      // 重置密码
      {
        path: 'account/reset-password',
        name: 'ResetPassword',
        meta: { titleKey: 'route.resetPassword', title: '重置密码' },
        component: () => import('@/views/userCenter/account/ResetPassword.vue')
      },
      // 我的授权
      {
        path: 'account/my-approval',
        name: 'UserCenterMyApproval',
        meta: { titleKey: 'route.myAuthorization', title: '我的授权' },
        component: () => import('@/views/userCenter/myApproval/MyApproval.vue')
      },
      // 免费权益
      {
        path: 'rights/free-rights',
        name: 'FreeRights',
        meta: { titleKey: 'route.freeRights', title: '新用户免费权益' },
        component: () => import('@/views/userCenter/rights/FreeRights.vue')
      },
      // 平台大会员套餐
      {
        path: 'rights/platform-vip',
        name: 'PlatformVip',
        meta: { titleKey: 'route.platformVip', title: '平台大会员套餐' },
        component: () => import('@/views/userCenter/rights/PlatformVipPackage.vue')
      },
      // 版权商业会员
      {
        path: 'rights/copyright-business',
        name: 'CopyrightBusiness',
        meta: { titleKey: 'route.businessMember', title: '版权商业会员' },
        component: () => import('@/views/userCenter/rights/CopyrightBusinessMember.vue')
      },
      // 邀请用户领取权益
      {
        path: 'rights/invite-user',
        name: 'InviteUser',
        meta: { titleKey: 'route.inviteUser', title: '邀请用户领取权益' },
        component: () => import('@/views/userCenter/rights/InviteUserRights.vue')
      },
      // 教育培训权益
      {
        path: 'rights/education-training',
        name: 'EducationTraining',
        meta: { titleKey: 'route.educationRights', title: '教育培训权益' },
        component: () => import('@/views/userCenter/rights/EducationTrainingRights.vue')
      },
      // 版权公益行动
      {
        path: 'rights/copyright-public-action',
        name: 'CopyrightPublicAction',
        meta: { titleKey: 'route.copyrightPublicAction', title: '版权公益行动' },
        component: () => import('@/views/userCenter/rights/CopyrightPublicAction/CopyrightPublicAction.vue')
      },
      // 我的记录
      {
        path: 'my-record',
        name: 'MyRecord',
        meta: { titleKey: 'route.myRecord', title: '我的记录' },
        component: () => import('@/views/userCenter/myRecord/MyRecord.vue')
      },
      // 我的订单
      {
        path: 'order',
        name: 'MyOrder',
        meta: { titleKey: 'route.myOrder', title: '我的订单' },
        component: () => import('@/views/userCenter/order/MyOrder.vue')
      },
      // 我的发票
      {
        path: 'invoice',
        name: 'MyInvoice',
        meta: { titleKey: 'route.myInvoice', title: '我的发票' },
        component: () => import('@/views/userCenter/invoice/MyInvoice.vue')
      },
      // 我的咨询
      {
        path: 'my-consultation',
        name: 'MyConsultation',
        meta: { titleKey: 'route.myConsultation', title: '我的咨询' },
        component: () => import('@/views/userCenter/myConsultation/MyConsultation.vue')
      },
      // 我的活动
      {
        path: 'activity',
        name: 'MyActivity',
        meta: { titleKey: 'route.myActivity', title: '我的活动' },
        component: () => import('@/views/userCenter/activity/MyActivity.vue')
      },
      // 我的收藏：暂不开放
      // {
      //   path: 'collection',
      //   name: 'MyCollection',
      //   meta: { titleKey: 'route.myFavorite', title: '我的收藏' },
      //   component: () => import('@/views/userCenter/collection/MyCollection.vue')
      // },
      // 消息中心
      {
        path: 'message',
        name: 'MessageCenter',
        meta: { titleKey: 'route.messageCenter', title: '消息中心' },
        component: () => import('@/views/userCenter/message/MessageCenter.vue')
      },
    ]
  },

  // ---------- 独立入口（需登录） ----------

  // ---------- 文章详情页（通用组件，无需登录） ----------
  {
    path: '/article/:id?',
    name: 'ArticleDetail',
    component: () => import('@/views/article/ArticleDetail.vue'),
    meta: { titleKey: 'route.contentDisplay', title: '内容展示', requiresAuth: false }
  },

  // ---------- 栏目内容详情页（无需登录） ----------
  {
    path: '/copyright-services/column-content/detail',
    name: 'ColumnContentDetail',
    component: () => import('@/views/copyrightService/ColumnContentDetail.vue'),
    meta: { titleKey: 'route.columnContentDetail', title: '栏目内容详情', requiresAuth: false }
  },

  // ---------- 知识产权管理-使用操作指南（无需登录，仅头部+富文本） ----------
  {
    path: '/copyright-protection/intellectual-property-management/operate-guide',
    name: 'IpmOperateIntroduce',
    component: () => import('@/views/copyrightProtection/IntellectualPropertyManagement/ipmOperateIntroduce.vue'),
    meta: { titleKey: 'route.operationGuide', title: '使用操作指南', requiresAuth: false }
  },

  // ---------- 为你推荐详情页（无需登录） ----------
  {
    path: '/recommend/detail',
    name: 'RecommendDetail',
    component: () => import('@/views/RecommendDetail.vue'),
    meta: { titleKey: 'route.recommendDetail', title: '推荐详情', requiresAuth: false }
  },

  // ---------- 页脚富文本内容（无需登录） ----------
  {
    path: '/footer/rich-content',
    name: 'FooterRichContent',
    component: () => import('@/views/commonHeaderAndFooter/FooterRichContent.vue'),
    meta: { titleKey: 'route.contentDetail', title: '内容详情', requiresAuth: false }
  },

  // ---------- 用户注册协议（无需登录） ----------
  {
    path: '/user-register-agreement',
    name: 'UserRegisterAgreement',
    component: () => import('@/views/login/UserRegisterAgreement.vue'),
    meta: { titleKey: 'route.registerAgreement', title: '深圳市版权公共服务平台用户注册协议', requiresAuth: false }
  },

  // ---------- 隐私政策（无需登录） ----------
  {
    path: '/user-privacy-policy',
    name: 'UserPrivacyPolicy',
    component: () => import('@/views/login/UserPrivacyPolicy.vue'),
    meta: { titleKey: 'route.privacyPolicy', title: '深圳市版权公共服务平台隐私政策', requiresAuth: false }
  },

  // ---------- 知识产权管理数据看板（独立全页，由介绍页「前往知识产权管理」进入） ----------
  {
    path: '/intellectual-property-management',
    name: 'IntellectualPropertyManagement',
    meta: { titleKey: 'route.ipManagement', title: '知识产权管理' },
    component: () => import('@/views/copyrightProtection/IntellectualPropertyManagement/IntellectualPropertyManagement.vue')
  },

  // ---------- 工作台（需登录） ----------
  // 企业工作台
  {
    path: '/workbench',
    name: 'Workbench',
    component: Workbench,
    meta: { titleKey: 'route.enterpriseWorkbench', title: '企业工作台', requiresAuth: true }
  },

  // ---------- 签名页（无需登录） ----------
  // 电子签名
  {
    path: '/signature',
    name: 'Signature',
    component: Signature,
    meta: { titleKey: 'route.eSignature', title: '电子签名', requiresAuth: false }
  },

  // ---------- 证书报告验证页（无需登录） ----------
  {
    path: '/certificate-report-verification',
    name: 'CertificateReportVerification',
    component: () => import('@/views/certificateReportVerification/certificateReportVerificationIndex.vue'),
    meta: { titleKey: 'route.certificateReportVerification', title: '证书及报告验证', requiresAuth: false }
  },

   // ---------- 国际版验证落地页（无需登录） ----------
   {
    path: '/international-verification',
    name: 'InternationalVerification',
    component: () => import('@/views/certificateReportVerification/internationalReportVerification/InternationalVerificationIndex.vue'),
    meta: { titleKey: 'route.ipgoInternationalVerification', title: 'IPGO International Verification', requiresAuth: false }
  },

  // ---------- 国际版：通过证书编号直接打开验证结果页（无需登录） ----------
  // 例如：https://xxxxx/#/internationalFilingVerify/SCSP-CRCI-2682606-4076
  {
    path: '/internationalFilingVerify/:certificateNo',
    name: 'InternationalFilingVerifyPage',
    component: () => import('@/views/certificateReportVerification/internationalReportVerification/InternationalFilingVerifyPage.vue'),
    meta: { titleKey: 'route.ipgoDigitalRecordVerification', title: 'IPGO Digital Record Verification', requiresAuth: false }
  },

  // ---------- 数字备案信息验证分享页（无需登录） ----------
  // 通过路径 + id 打开分享页面，例如：https://xxxxx/filingCodeIndex/123
  {
    path: '/filingCodeIndex/:id',
    name: 'RecordSharePage',
    component: () => import('@/views/userCenter/myRecord/RecordSharePage.vue'),
    meta: { titleKey: 'route.digitalFilingVerification', title: '数字备案信息验证', requiresAuth: false }
  },
  {
    path: '/codeIndex/:id',
    name: 'LegacyMigrationFilingCodeIndex',
    component: () => import('@/views/certificateReportVerification/LegacyMigrationFilingCodeIndex.vue'),
    meta: { titleKey: 'route.digitalFilingVerification', title: '数字备案信息验证', requiresAuth: false }
  },

  // ---------- 国内版：通过证书编号直接打开数字备案验证页（无需登录） ----------
  // 例如：https://xxxxx/filingVerify/SCSP-CRCI-2682606-4076
  {
    path: '/filingVerify/:certificateNo',
    name: 'FilingVerifyPage',
    component: () => import('@/views/userCenter/myRecord/FilingVerifyPage.vue'),
    meta: { titleKey: 'route.digitalFilingVerification', title: '数字备案信息验证', requiresAuth: false }
  },

  // ---------- 国际版数字备案信息验证分享页（无需登录） ----------
  // 通过路径 + id 打开分享页面，例如：https://xxxxx/#/internationalFilingCodeIndex/123
  {
    path: '/internationalFilingCodeIndex/:id',
    name: 'InternationalRecordSharePage',
    component: () => import('@/views/certificateReportVerification/internationalReportVerification/InternationalRecordSharePage.vue'),
    meta: { titleKey: 'route.ipgoDigitalRecordVerification', title: 'IPGO Digital Record Verification', requiresAuth: false }
  },

  // 通过路径 + id 打开分享页面，例如：https://xxxxx/#/codeIndex/123
  {
    path: '/authOrTransIndex/:id',
    name: 'AuthOrTransPage',
    component: () => import('@/views/userCenter/myRecord/AuthOrTransPage.vue'),
    meta: { titleKey: 'route.digitalFilingVerification', title: '数字备案信息验证', requiresAuth: false }
  },

  // ---------- 存证报告详情分享页（无需登录） ----------
  {
    path: '/evidenceReport/:reportNumber',
    name: 'EvidenceReportDetailPage',
    component: () => import('@/views/copyrightProtection/evidenceCollection/EvidenceReportDetailPage.vue'),
    meta: { titleKey: 'route.evidenceReportDetail', title: '存证报告详情', requiresAuth: false }
  },

  // ---------- 版权监测报告详情分享页（无需登录） ----------
  {
    path: '/copyrightMonitoringReport/:reportNumber',
    name: 'CopyrightWorksMonitoringReportPage',
    component: () => import('@/views/copyrightProtection/copyrightRiskMonitoring/modules/CopyrightWorksMonitoringReportPage.vue'),
    meta: { titleKey: 'route.monitoringReportDetail', title: '版权监测报告详情', requiresAuth: false }
  },

  // ---------- 未匹配路由回首页 ----------
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFoundRedirect',
    redirect: DEFAULT_HOME
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})


installChunkLoadErrorHandler(router)
// 路由守卫：维护模式 / 登录校验 / 未匹配路由回首页
router.beforeEach((to, from, next) => {
  if (MAINTENANCE_MODE && to.path !== '/maintenance') {
    next('/maintenance')
    return
  }
  // 兜底：无匹配路由时回首页（配合末尾 catch-all）
  if (!to.matched.length) {
    next(DEFAULT_HOME)
    return
  }
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isLoggedIn()) {
      ElMessage.warning(t('common.pleaseLoginFirst'))
      next(DEFAULT_HOME)
      return
    }
  }
  next()
})

router.afterEach(() => {
  updateDocumentTitle()
})

export default router
